import {
  ConsoleLogger,
  DefaultActiveSpeakerPolicy,
  DefaultDeviceController,
  DefaultMeetingSession,
  LogLevel,
  MeetingSessionStatusCode,
  MeetingSessionConfiguration,
  type AudioVideoFacade,
  type AudioVideoObserver,
  type ContentShareObserver,
  type MeetingSessionStatus,
  type VideoTileState,
} from 'amazon-chime-sdk-js';
import { notify } from '@/lib/notify';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChimeAttendeePayload, ChimeMeetingPayload } from './chimeTelehealthService';
import { NO_MICROPHONE_TEXT } from './chimeDevices';
import { wasMeetingEndedByHost } from './chimeMeetingStatus';
import { recorderIdsAfterPresence } from './chimeRecording';
import {
  deactivateChimeTile,
  removeChimeTilesForAttendee,
  upsertChimeTile,
  visibleChimeTiles,
  withCameraOffPlaceholders,
  type ChimeTile,
} from './chimeTiles';

export type ChimeMeetingStatus = 'idle' | 'starting' | 'in-meeting' | 'ended' | 'error';

/** `requireAudio`: fail the start when there is no `audioDeviceId` rather than join with a silent microphone. */
export type ChimeStartDevices = {
  audioDeviceId?: string;
  videoDeviceId?: string;
  requireAudio?: boolean;
  /** Chosen on the join screen: join muted, or with the camera off (it can be turned on in the call). */
  startMuted?: boolean;
  cameraOff?: boolean;
};

/**
 * Upper bound on waiting for the SDK's leave/ack before we release the microphone.
 * A live signaling channel acks in well under a second; a dead one fails fast.
 */
const DISCONNECT_WAIT_MS = 3_000;

/** Pause, detach, and stop tracks so Chrome drops the tab speaker icon. */
function releaseMediaElement(el: HTMLMediaElement | null): void {
  if (!el) return;
  try {
    const stream = el.srcObject;
    if (stream instanceof MediaStream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  } catch {
    /* srcObject already detached */
  }
  el.pause();
  el.srcObject = null;
}

/** Resolves once the SDK reports it has disconnected (peer connection closed), or on timeout. */
function waitForAudioVideoStop(av: AudioVideoFacade, timeoutMs: number): Promise<void> {
  return new Promise((resolve) => {
    const observer: AudioVideoObserver = { audioVideoDidStop: () => done() };
    const timer = setTimeout(done, timeoutMs);
    function done(): void {
      clearTimeout(timer);
      av.removeObserver(observer);
      resolve();
    }
    av.addObserver(observer);
  });
}

export function useChimeMeetingSession(): {
  status: ChimeMeetingStatus;
  error: string | null;
  /** With `status: 'error'` after an unexpected SDK stop: the terminal `MeetingSessionStatusCode` (else undefined). */
  errorCode: MeetingSessionStatusCode | undefined;
  tiles: ChimeTile[];
  muted: boolean;
  cameraOn: boolean;
  remoteAttendeePresent: boolean;
  activeSpeakerId: string | undefined;
  /** People in the roster by attendee id; never the recorder. */
  attendeeExternalUserIds: Readonly<Record<string, string>>;
  /** The recorder (Chime's media capture pipeline attendee) is in the meeting: the session is recording. */
  recorderPresent: boolean;
  endedByHost: boolean;
  attendeeId: string | undefined;
  /** This browser is sharing its screen. */
  sharing: boolean;
  /** Video-only screen share (no shared audio); resolves false when refused or cancelled. */
  startScreenShare: () => Promise<boolean>;
  stopScreenShare: () => void;
  start: (
    meeting: ChimeMeetingPayload,
    attendee: ChimeAttendeePayload,
    devices?: ChimeStartDevices,
  ) => Promise<boolean>;
  stop: () => void;
  toggleMute: () => void;
  toggleCamera: () => Promise<void>;
  /** Switches the microphone mid-call (the in-person page's picker). */
  setAudioInput: (deviceId: string) => Promise<void>;
  bindAudio: (el: HTMLAudioElement | null) => void;
  bindTile: (tileId: number, el: HTMLVideoElement | null) => void;
} {
  const sessionRef = useRef<DefaultMeetingSession | null>(null);
  const audioVideoRef = useRef<AudioVideoFacade | null>(null);
  const deviceControllerRef = useRef<DefaultDeviceController | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const observerRef = useRef<AudioVideoObserver | null>(null);
  const presenceUnsubRef = useRef<(() => void) | null>(null);
  const contentObserverRef = useRef<ContentShareObserver | null>(null);
  const shareStreamRef = useRef<MediaStream | null>(null);
  const activeSpeakerUnsubRef = useRef<(() => void) | null>(null);
  const videoDeviceIdRef = useRef<string | undefined>();
  const videoElsRef = useRef<Map<number, HTMLVideoElement>>(new Map());
  // Bumped when a teardown starts and again when a new start supersedes it, so an
  // in-flight teardown does not close the AudioContext of the meeting that just started.
  const releaseGenerationRef = useRef(0);
  // Bumped by every stop/start and on unmount, so a start that is still awaiting devices
  // or the SDK can tell it was superseded and must not join the meeting.
  const startGenerationRef = useRef(0);
  const mountedRef = useRef(true);

  const [status, setStatus] = useState<ChimeMeetingStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<MeetingSessionStatusCode | undefined>();
  const [tiles, setTiles] = useState<ChimeTile[]>([]);
  const [muted, setMuted] = useState(false);
  const [cameraOn, setCameraOn] = useState(false);
  const [remoteAttendeePresent, setRemoteAttendeePresent] = useState(false);
  const [activeSpeakerId, setActiveSpeakerId] = useState<string | undefined>();
  const [attendeeExternalUserIds, setAttendeeExternalUserIds] = useState<
    Record<string, string>
  >({});
  const [endedByHost, setEndedByHost] = useState(false);
  const [attendeeId, setAttendeeId] = useState<string | undefined>();
  const [sharing, setSharing] = useState(false);
  const [absentAttendeeIds, setAbsentAttendeeIds] = useState<ReadonlySet<string>>(new Set());
  // The recorder's attendee ids (normally one); a ref so a presence event can tell at once whether it is the recorder.
  const recorderIdsRef = useRef<ReadonlySet<string>>(new Set());
  const [recorderPresent, setRecorderPresent] = useState(false);

  /**
   * `sessionStopped` is true when the SDK already reported `audioVideoDidStop`
   * (host ended the meeting), so there is no disconnect left to wait for.
   */
  const teardown = useCallback((sessionStopped: boolean) => {
    presenceUnsubRef.current?.();
    presenceUnsubRef.current = null;
    activeSpeakerUnsubRef.current?.();
    activeSpeakerUnsubRef.current = null;
    const session = sessionRef.current;
    const av = audioVideoRef.current;
    const deviceController = deviceControllerRef.current;
    sessionRef.current = null;
    audioVideoRef.current = null;
    deviceControllerRef.current = null;
    if (av && observerRef.current) {
      av.removeObserver(observerRef.current);
    }
    observerRef.current = null;
    if (av && contentObserverRef.current) {
      av.removeContentShareObserver(contentObserverRef.current);
      try {
        av.stopContentShare();
      } catch {
        /* not sharing */
      }
    }
    contentObserverRef.current = null;
    shareStreamRef.current?.getTracks().forEach((track) => track.stop());
    shareStreamRef.current = null;
    setSharing(false);
    // Release while srcObject is still set. unbindAudioElement nulls it without
    // stopping tracks, and a detached element keeps the tab icon in Chrome.
    const audioEl = audioElRef.current;
    audioElRef.current = null;
    releaseMediaElement(audioEl);
    for (const el of videoElsRef.current.values()) releaseMediaElement(el);
    videoElsRef.current.clear();
    setTiles([]);
    setRemoteAttendeePresent(false);
    setActiveSpeakerId(undefined);
    setAttendeeExternalUserIds({});
    setAbsentAttendeeIds(new Set());
    recorderIdsRef.current = new Set();
    setRecorderPresent(false);
    setStatus((prev) => (prev === 'idle' ? prev : 'ended'));
    // Nothing else to release (already torn down, or start never got this far). Return
    // before bumping the generation so a repeat stop() cannot cancel the pending close below.
    if (!av) return;

    const generation = ++releaseGenerationRef.current;
    // Register before av.stop() so the disconnect notification cannot be missed.
    const disconnected = sessionStopped
      ? Promise.resolve()
      : waitForAudioVideoStop(av, DISCONNECT_WAIT_MS);
    try {
      av.unbindAudioElement();
    } catch {
      /* nothing bound */
    }
    try {
      av.stopLocalVideoTile();
    } catch {
      /* no local tile */
    }
    try {
      av.stop();
    } catch {
      /* already stopped */
    }
    // Silences the shared context right away when one already exists (no-mic joins).
    void DefaultDeviceController.suspendAudioContext();
    void (async () => {
      try {
        await av.stopVideoInput();
      } catch {
        /* no video input */
      }
      // Do not stop the audio input while the peer connection is still open. The SDK
      // reacts to a stopped microphone by synthesizing an empty one on its shared
      // AudioContext — creating that context, running, if a real microphone meant it
      // never existed — which is exactly what keeps Chrome's tab speaker icon lit.
      await disconnected;
      try {
        await av.stopAudioInput();
      } catch {
        /* no audio input */
      }
      try {
        await session?.destroy();
      } catch {
        /* already destroyed */
      }
      try {
        await deviceController?.destroy();
      } catch {
        /* already destroyed */
      }
      if (generation !== releaseGenerationRef.current) return;
      // Closing (not suspending) releases the browser's audio output stream; the SDK
      // lazily recreates the context for the next meeting.
      DefaultDeviceController.closeAudioContext();
    })();
  }, []);

  const stop = useCallback(() => {
    startGenerationRef.current += 1;
    teardown(false);
  }, [teardown]);

  const start = useCallback(
    /** Resolves true once in the meeting; false when the join failed or was superseded. */
    async (
      meeting: ChimeMeetingPayload,
      attendee: ChimeAttendeePayload,
      devices?: ChimeStartDevices,
    ): Promise<boolean> => {
      if (!mountedRef.current) return false;
      stop();
      const generation = ++startGenerationRef.current;
      const superseded = (): boolean =>
        !mountedRef.current || generation !== startGenerationRef.current;
      // The stop above schedules a close. Cancel it so it cannot run against the
      // context this meeting is about to use; resume covers the eager suspend.
      releaseGenerationRef.current += 1;
      await DefaultDeviceController.resumeAudioContext();
      if (superseded()) return false;
      setStatus('starting');
      setError(null);
      setErrorCode(undefined);
      setEndedByHost(false);
      setAttendeeId(attendee.AttendeeId);
      if (attendee.AttendeeId && attendee.ExternalUserId) {
        setAttendeeExternalUserIds({ [attendee.AttendeeId]: attendee.ExternalUserId });
      }
      videoDeviceIdRef.current = devices?.videoDeviceId;
      let abandon: () => Promise<void> = async () => undefined;
      try {
        // Without a device the SDK joins with its empty (silent) input; a caller that needs audio fails instead.
        if (devices?.requireAudio && !devices.audioDeviceId) throw new Error(NO_MICROPHONE_TEXT);
        const logger = new ConsoleLogger('UnifyChime', LogLevel.WARN);
        const deviceController = new DefaultDeviceController(logger);
        const configuration = new MeetingSessionConfiguration(meeting, attendee);
        const meetingSession = new DefaultMeetingSession(configuration, logger, deviceController);
        const audioVideo = meetingSession.audioVideo;
        sessionRef.current = meetingSession;
        audioVideoRef.current = audioVideo;
        deviceControllerRef.current = deviceController;
        // A superseding stop() already tore down these refs, but devices acquired after
        // that teardown are still open and must be released here.
        abandon = async () => {
          try {
            audioVideo.stop();
          } catch {
            /* never started */
          }
          await audioVideo.stopVideoInput().catch(() => undefined);
          await audioVideo.stopAudioInput().catch(() => undefined);
          await meetingSession.destroy().catch(() => undefined);
          await deviceController.destroy().catch(() => undefined);
        };

        const observer: AudioVideoObserver = {
          audioVideoDidStop: (sessionStatus: MeetingSessionStatus) => {
            const code = sessionStatus.statusCode();
            const hostEnded = wasMeetingEndedByHost(code);
            const unexpected =
              !hostEnded && sessionStatus.isTerminal() && code !== MeetingSessionStatusCode.Left;
            if (hostEnded) setEndedByHost(true);
            // The host ending the meeting stops media without unmounting this hook, so the
            // client tab would otherwise keep the peer connection and the speaker icon.
            teardown(true);
            if (unexpected) {
              setError('The video connection ended unexpectedly.');
              setErrorCode(code);
              setStatus('error');
            }
          },
          videoTileDidUpdate: (tileState: VideoTileState) => {
            if (tileState.tileId == null) return;
            setTiles((prev) =>
              upsertChimeTile(prev, {
                tileId: tileState.tileId!,
                boundAttendeeId: tileState.boundAttendeeId ?? undefined,
                localTile: Boolean(tileState.localTile),
                active: Boolean(tileState.active),
                ...(tileState.isContent ? { isContent: true } : {}),
              }),
            );
          },
          videoTileWasRemoved: (tileId: number) => {
            setTiles((prev) => deactivateChimeTile(prev, tileId));
          },
        };
        observerRef.current = observer;
        audioVideo.addObserver(observer);
        // Also fires when the browser's own "Stop sharing" ends the capture, or a provider stops it.
        const contentObserver: ContentShareObserver = {
          contentShareDidStart: () => setSharing(true),
          contentShareDidStop: () => {
            shareStreamRef.current?.getTracks().forEach((track) => track.stop());
            shareStreamRef.current = null;
            setSharing(false);
          },
        };
        contentObserverRef.current = contentObserver;
        audioVideo.addContentShareObserver(contentObserver);
        const onPresence = (id: string, present: boolean, externalUserId?: string): void => {
          // The recorder is never a tile, a roster entry or a remote participant; any departure means not recording.
          const recorderIds = recorderIdsAfterPresence(recorderIdsRef.current, id, present, externalUserId);
          if (recorderIds) {
            recorderIdsRef.current = recorderIds;
            setRecorderPresent(recorderIds.size > 0);
            return;
          }
          setAttendeeExternalUserIds((prev) => {
            if (present && externalUserId) return { ...prev, [id]: externalUserId };
            if (present) return prev;
            return Object.fromEntries(
              Object.entries(prev).filter(([attendeeId]) => attendeeId !== id),
            );
          });
          setAbsentAttendeeIds((prev) => {
            const next = new Set(prev);
            if (present) next.delete(id);
            else next.add(id);
            return next;
          });
          if (!present) {
            setTiles((prev) => removeChimeTilesForAttendee(prev, id));
          }
          if (id === attendee.AttendeeId) return;
          setRemoteAttendeePresent(present);
        };
        audioVideo.realtimeSubscribeToAttendeeIdPresence(onPresence);
        presenceUnsubRef.current = () => {
          audioVideo.realtimeUnsubscribeToAttendeeIdPresence(onPresence);
        };
        const onActiveSpeakers = (ids: string[]): void => {
          setActiveSpeakerId(ids.find((id) => id !== attendee.AttendeeId) ?? ids[0]);
        };
        audioVideo.subscribeToActiveSpeakerDetector(new DefaultActiveSpeakerPolicy(), onActiveSpeakers);
        activeSpeakerUnsubRef.current = () => {
          audioVideo.unsubscribeFromActiveSpeakerDetector(onActiveSpeakers);
        };

        if (devices?.audioDeviceId) {
          await audioVideo.startAudioInput(devices.audioDeviceId);
        } else {
          await audioVideo.startAudioInput(null);
        }
        if (superseded()) {
          await abandon();
          return false;
        }
        const videoStarted = Boolean(devices?.videoDeviceId) && !devices?.cameraOff;
        if (videoStarted && devices?.videoDeviceId) {
          await audioVideo.startVideoInput(devices.videoDeviceId);
        }
        if (superseded()) {
          await abandon();
          return false;
        }
        if (audioElRef.current) {
          audioVideo.bindAudioElement(audioElRef.current);
        }
        audioVideo.start();
        if (videoStarted) audioVideo.startLocalVideoTile();
        if (devices?.startMuted) audioVideo.realtimeMuteLocalAudio();
        setCameraOn(videoStarted);
        setMuted(Boolean(devices?.startMuted));
        setStatus('in-meeting');
        return true;
      } catch (e) {
        if (superseded()) {
          await abandon();
          return false;
        }
        stop();
        setStatus('error');
        setError(e instanceof Error ? e.message : 'Could not start the Chime session');
        return false;
      }
    },
    [stop, teardown],
  );

  const toggleMute = useCallback(() => {
    const av = audioVideoRef.current;
    if (!av) return;
    if (av.realtimeIsLocalAudioMuted()) {
      av.realtimeUnmuteLocalAudio();
      setMuted(false);
    } else {
      av.realtimeMuteLocalAudio();
      setMuted(true);
    }
  }, []);

  const toggleCamera = useCallback(async () => {
    const av = audioVideoRef.current;
    if (!av) return;
    if (cameraOn) {
      av.stopLocalVideoTile();
      setCameraOn(false);
      await av.stopVideoInput().catch(() => undefined);
      return;
    }
    const id = videoDeviceIdRef.current;
    try {
      if (!id) throw new Error('No camera selected');
      await av.startVideoInput(id);
      av.startLocalVideoTile();
      setCameraOn(true);
    } catch {
      notify({
        type: 'error',
        message: 'Could not turn on your camera. Check that it is connected and not in use by another app.',
      });
    }
  }, [cameraOn]);

  const startScreenShare = useCallback(async (): Promise<boolean> => {
    const av = audioVideoRef.current;
    if (!av) return false;
    try {
      // Video only: a shared tab's audio must never reach the call (or the AudioOnly capture).
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      if (audioVideoRef.current !== av) {
        stream.getTracks().forEach((track) => track.stop());
        return false;
      }
      shareStreamRef.current = stream;
      await av.startContentShare(stream);
      return true;
    } catch {
      // Cancelled in the browser picker, or refused (e.g. the provider stopped this attendee's share).
      shareStreamRef.current?.getTracks().forEach((track) => track.stop());
      shareStreamRef.current = null;
      return false;
    }
  }, []);

  const stopScreenShare = useCallback(() => {
    audioVideoRef.current?.stopContentShare();
  }, []);

  const setAudioInput = useCallback(async (deviceId: string) => {
    await audioVideoRef.current?.startAudioInput(deviceId);
  }, []);

  const bindAudio = useCallback((el: HTMLAudioElement | null) => {
    const previous = audioElRef.current;
    if (previous && audioVideoRef.current) {
      audioVideoRef.current.unbindAudioElement();
    }
    audioElRef.current = el;
    if (el && audioVideoRef.current) {
      audioVideoRef.current.bindAudioElement(el);
    }
  }, []);

  const bindTile = useCallback((tileId: number, el: HTMLVideoElement | null) => {
    if (el) videoElsRef.current.set(tileId, el);
    else videoElsRef.current.delete(tileId);
    const av = audioVideoRef.current;
    if (!av) return;
    if (el) {
      av.bindVideoElement(tileId, el);
    } else {
      av.unbindVideoElement(tileId);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const onHide = (): void => {
      stop();
    };
    window.addEventListener('pagehide', onHide);
    return () => {
      mountedRef.current = false;
      window.removeEventListener('pagehide', onHide);
      stop();
    };
  }, [stop]);

  const presentTiles = useMemo(
    () =>
      withCameraOffPlaceholders(
        visibleChimeTiles(tiles, absentAttendeeIds),
        Object.keys(attendeeExternalUserIds),
        attendeeId,
      ),
    [tiles, absentAttendeeIds, attendeeExternalUserIds, attendeeId],
  );

  return {
    status,
    error,
    errorCode,
    tiles: presentTiles,
    muted,
    cameraOn,
    remoteAttendeePresent,
    activeSpeakerId,
    attendeeExternalUserIds,
    recorderPresent,
    endedByHost,
    attendeeId,
    sharing,
    startScreenShare,
    stopScreenShare,
    start,
    stop,
    toggleMute,
    toggleCamera,
    setAudioInput,
    bindAudio,
    bindTile,
  };
}
